import { c as _c } from "react/compiler-runtime";
import { CheckIcon } from 'lucide-react';
import { useId } from 'react';
import type { PlanComparisonCard } from '../plan-comparison';
import { PlanFeature } from './plan-feature';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RollingNumber } from '@/components/ui/rolling-number';
import { cn } from '@/lib/utils';
type PlanCardProps = {
  card: PlanComparisonCard;
  isSelected?: boolean;
  isCurrentPlan?: boolean;
  onCtaClick: () => void;
};
export function PlanCard(t0) {
  const $ = _c(39);
  const {
    card,
    isSelected,
    isCurrentPlan,
    onCtaClick
  } = t0;
  const isDisabled = isCurrentPlan;
  const isRecommended = card.isRecommended;
  const isTeamPlan = card.plan === "team";
  const hasActiveOutline = card.isHighlighted || isSelected;
  const usePrimaryButton = hasActiveOutline;
  const featuresHeadingId = useId();
  const frameClassName = hasActiveOutline ? "border-solid border-primary/70 bg-gradient-to-b from-primary/[0.08] to-[#101011]" : isTeamPlan ? "border-dashed border-border" : "border-border";
  let t1;
  if ($[0] !== frameClassName) {
    t1 = cn("relative rounded-xl border bg-[#101011] p-6 transition-colors duration-200", frameClassName);
    $[0] = frameClassName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== card.title) {
    t2 = <p className="text-2xl">{card.title}</p>;
    $[2] = card.title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isRecommended) {
    t3 = isRecommended && <Badge className="shrink-0">Recommended</Badge>;
    $[4] = isRecommended;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex items-start justify-between gap-3">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== card.tagline) {
    t5 = <p className="mt-1 text-sm text-zinc-400">{card.tagline}</p>;
    $[9] = card.tagline;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== card.headline) {
    t6 = <p className="mt-2 text-3xl font-medium tabular-nums leading-none sm:text-5xl"><RollingNumber value={card.headline} /></p>;
    $[11] = card.headline;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== card.priceDetail) {
    t7 = <p className="mt-2 text-base text-zinc-400">{card.priceDetail}</p>;
    $[13] = card.priceDetail;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t7) {
    t8 = <header className="mb-4">{t4}{t5}{t6}{t7}</header>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== card.ctaHref || $[21] !== card.ctaLabel || $[22] !== isDisabled || $[23] !== onCtaClick || $[24] !== usePrimaryButton) {
    t9 = <div className="mb-6">{card.ctaHref && !isDisabled ? <Button variant={usePrimaryButton ? "default" : "outline"} size="lg" className="w-full" asChild={true}><a href={card.ctaHref}>{card.ctaLabel}</a></Button> : <Button variant={usePrimaryButton ? "default" : "outline"} size="lg" className="w-full" disabled={isDisabled} onClick={onCtaClick}>{card.ctaLabel}</Button>}</div>;
    $[20] = card.ctaHref;
    $[21] = card.ctaLabel;
    $[22] = isDisabled;
    $[23] = onCtaClick;
    $[24] = usePrimaryButton;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== card.featuresHeading || $[27] !== featuresHeadingId) {
    t10 = card.featuresHeading && <p id={featuresHeadingId} className="mb-3 text-sm text-zinc-400">{card.featuresHeading}</p>;
    $[26] = card.featuresHeading;
    $[27] = featuresHeadingId;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== card.features || $[30] !== card.featuresHeading || $[31] !== featuresHeadingId) {
    t11 = card.features.length > 0 && <ul className="space-y-3" aria-labelledby={card.featuresHeading ? featuresHeadingId : undefined}>{card.features.map(_temp)}</ul>;
    $[29] = card.features;
    $[30] = card.featuresHeading;
    $[31] = featuresHeadingId;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== t1 || $[34] !== t10 || $[35] !== t11 || $[36] !== t8 || $[37] !== t9) {
    t12 = <article className={t1}>{t8}{t9}{t10}{t11}</article>;
    $[33] = t1;
    $[34] = t10;
    $[35] = t11;
    $[36] = t8;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
}
function _temp(feature) {
  return <li key={typeof feature === "string" ? feature : feature.label} className="flex min-h-6 items-start gap-2 text-sm font-normal text-zinc-300"><span className="flex h-[1lh] w-4 shrink-0 items-center justify-center"><CheckIcon className="size-4 text-zinc-400" aria-hidden="true" /></span><PlanFeature feature={feature} /></li>;
}